import React, { useState } from 'react';
import { useDashboardData } from '../hooks/useDashboardData';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import { TrendingUp, TrendingDown, Calendar, DollarSign, Package, ChevronRight, ChevronDown, Eye, EyeOff, Store } from 'lucide-react';
import { LoadingScreen } from '../../../components/ui/loading';
import LightningLotsAlert from './LightningLotsAlert';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { PendingDeliveries } from './pending/PendingDeliveries';
import { CajaAlert } from './caja/CajaAlert';
import { TeamStatus } from './team/TeamStatus';
import { PrinterStatus } from './printer/PrinterStatus';
import { EnvasesContainer } from './containers/EnvasesContainer';
import { CajaContainer } from './containers/CajaContainer';
import { useNavigate } from 'react-router-dom';
import { ForumWidget } from './forum/ForumWidget';
import { CatalogWidget } from './catalog/CatalogWidget';

const MetricCard = ({ title, value, trend }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</div>
      <div className="space-y-1">
        <div className="text-2xl font-bold dark:text-white">{value}</div>
        {/* Comentado temporalmente
        {trend && (
          <div className={`flex items-center text-sm ${
            isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}>
            {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {trend} vs. período anterior
          </div>
        )}
        */}
      </div>
    </div>
  );
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
};

// Utility function to format numbers
const formatNumber = (value) => {
  if (value >= 1000000) {
    return {
      short: `${(value / 1000000).toFixed(1)}M`,
      full: value.toLocaleString()
    };
  }
  if (value >= 1000) {
    return {
      short: `${(value / 1000).toFixed(1)}K`,
      full: value.toLocaleString()
    };
  }
  return {
    short: value.toString(),
    full: value.toLocaleString()
  };
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, type }) => {
  if (!active || !payload || !payload.length) return null;

  const currentValue = payload[0].value;
  const previousValue = payload[1]?.value;
  const formattedCurrent = formatNumber(currentValue);
  
  return (
    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border dark:border-gray-700">
      <div className="font-medium dark:text-white">{label}</div>
      <div className="space-y-1 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full" />
          <div className="dark:text-gray-200">
            {type === 'sales' ? '$' : ''}{formattedCurrent.short}
            <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">({formattedCurrent.full})</span>
          </div>
        </div>
        {previousValue && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full" />
            <div>Período anterior: {type === 'sales' ? '$' : ''}{formatNumber(previousValue).short}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced chart component
const EnhancedChart = ({ data, previousData, type, timeRange }) => {
  const yAxisFormatter = (value) => formatNumber(value).short;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Evolución de {type === 'sales' ? 'Ventas' : 'Bultos'}
        </div>
        <div className="flex gap-2 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full" />
            <span className="dark:text-gray-300">Actual</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full" />
            <span className="dark:text-gray-300">Anterior</span>
          </div>
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="dark:opacity-20" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: 'currentColor' }}
              tickFormatter={(value) => {
                const date = new Date(value);
                if (timeRange === 'today') {
                  return date.toLocaleTimeString('es-AR', { 
                    hour: '2-digit', 
                    minute: '2-digit'
                  });
                }
                return date.toLocaleDateString('es-AR', {
                  day: '2-digit',
                  month: 'short'
                });
              }}
              className="dark:text-gray-400"
            />
            <YAxis 
              tickFormatter={yAxisFormatter}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              width={60}
              className="dark:text-gray-400"
            />
            <Tooltip content={(props) => <CustomTooltip {...props} type={type} />} />
            <Bar 
              dataKey={type === 'sales' ? 'amount' : 'quantity'} 
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
            {previousData && (
              <Line
                type="monotone"
                data={previousData}
                dataKey={type === 'sales' ? 'amount' : 'quantity'}
                stroke="#9ca3af"
                strokeDasharray="5 5"
                dot={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export function DashboardScreen() {
  const { user } = useAuth();
  const { business } = useBusiness();
  // Inicializar showAlerts desde localStorage, por defecto true
  const [showAlerts, setShowAlerts] = useState(() => {
    const saved = localStorage.getItem('dashboard_show_alerts');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [timeRange, setTimeRange] = useState('today');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
  const { data, loading, error } = useDashboardData(timeRange, customDateRange);
  const [chartType, setChartType] = useState('sales'); // 'sales' or 'bultos'
  const navigate = useNavigate();

  // Al inicio del componente
  console.log('=== DEBUG DASHBOARD ===');
  console.log('User actual:', {
    id: user?.id,
    email: user?.email,
    business_id: user?.business_id,
    metadata: user?.user_metadata
  });
  console.log('=======================');

  // Guardar en localStorage cuando cambia showAlerts
  const handleToggleAlerts = () => {
    const newValue = !showAlerts;
    setShowAlerts(newValue);
    localStorage.setItem('dashboard_show_alerts', JSON.stringify(newValue));
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-2">
          <p className="text-red-500 dark:text-red-400 font-medium">Error al cargar los datos</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full p-6 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {getGreeting()}, {user?.user_metadata?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Usuario'}
          </h1>
        </div>

        {/* Resumen de Actividad */}
        <div className="space-y-4 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div 
            className="flex justify-between items-center cursor-pointer"
            onClick={handleToggleAlerts}
          >
            <div className="flex items-center gap-3">
              <Store className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                Lo que está pasando en {business?.name && (
                  <><br /><span className="font-bold text-[#3B7C5B] text-2xl">{business.name}</span></>
                )}
              </h2>
            </div>
            <button
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500 dark:text-gray-400"
              title={showAlerts ? "Ocultar actividad" : "Mostrar actividad"}
            >
              {showAlerts ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {showAlerts && (
            <div className="space-y-3">
              <LightningLotsAlert />
              <PendingDeliveries />
              <CajaAlert />
            </div>
          )}
        </div>
      </div>

      {/* Panel de Componentes */}
      <div className="mt-8 space-y-6">
        {/* <div className="flex items-center gap-2 mb-6">
          <div className="flex items-center gap-2">
            <PrinterTestButton />
            <ESCPOSTestButton />
          </div>
        </div> */}

        {/* Grid de widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Widget de estado de impresora */}
          <PrinterStatus />
          
          {/* Widget de catálogo */}
          <CatalogWidget />
        </div>
        
        {/* Caja Container */}
        <CajaContainer data={{
          efectivo: data?.payments?.cash || 0,
          transferencias: data?.payments?.transfer || 0,
          credito: data?.payments?.credit || 0,
          otros: data?.payments?.other || 0,
          pendiente: {
            monto: data?.payments?.pending?.amount || 0,
            ventas: data?.payments?.pending?.count || 0
          }
        }} />

        {/* Time Range Selector */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm space-y-6">
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'today', label: 'Hoy' },
              { id: 'yesterday', label: 'Ayer' },
              { id: 'week', label: 'Esta semana' },
              { id: 'custom', icon: Calendar, tooltip: 'Personalizado' }
            ].map(range => (
              <button
                key={range.id}
                onClick={() => {
                  if (range.id === 'custom') {
                    setShowDatePicker(true);
                  } else {
                    setTimeRange(range.id);
                    setShowDatePicker(false);
                  }
                }}
                title={range.tooltip}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  timeRange === range.id
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {range.icon ? <range.icon className="w-4 h-4" /> : range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Picker Modal */}
        {showDatePicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full space-y-4">
              <h3 className="text-lg font-medium dark:text-white">Seleccionar rango de fechas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Desde</label>
                  <input
                    type="date"
                    className="w-full p-2 border dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-lg"
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Hasta</label>
                  <input
                    type="date"
                    className="w-full p-2 border dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-lg"
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setTimeRange('custom');
                    setShowDatePicker(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <MetricCard 
            title="Total Ventas" 
            value={`$${data?.totalSales?.toLocaleString() || '0'}`}
            trend={data?.trend?.sales}
          />
          <MetricCard 
            title="Total Bultos Vendidos" 
            value={data?.totalBultos?.toLocaleString() || '0'}
            trend={data?.trend?.bultos}
          />
        </div>

        {/* Chart */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Evolución de {chartType === 'sales' ? 'Ventas' : 'Bultos'}
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-full p-1 flex gap-1 shadow-sm">
              <button
                onClick={() => setChartType('sales')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                  chartType === 'sales'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                Ventas
              </button>
              <button
                onClick={() => setChartType('bultos')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                  chartType === 'bultos'
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Package className="w-4 h-4" />
                Bultos
              </button>
            </div>
          </div>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.chartData?.[chartType] || []} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.1} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (timeRange === 'today') {
                      return date.toLocaleTimeString('es-AR', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      });
                    }
                    return date.toLocaleDateString('es-AR', {
                      day: '2-digit',
                      month: 'short'
                    });
                  }}
                  stroke="#9CA3AF"
                  className="dark:text-gray-400"
                />
                <YAxis 
                  tickFormatter={(value) => formatNumber(value).short}
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  width={60}
                  stroke="#9CA3AF"
                  className="dark:text-gray-400"
                />
                <Tooltip content={(props) => <CustomTooltip {...props} type={chartType} />} />
                <Bar 
                  dataKey={chartType === 'sales' ? 'amount' : 'quantity'} 
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
                {data?.previousChartData?.[chartType] && (
                  <Line
                    type="monotone"
                    data={data.previousChartData[chartType]}
                    dataKey={chartType === 'sales' ? 'amount' : 'quantity'}
                    stroke="#9CA3AF"
                    strokeDasharray="5 5"
                    dot={false}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 justify-center mt-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span>Período actual</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full" />
              <span>Período anterior</span>
            </div>
          </div>
        </div>
      </div>

      {/* Forum Widget */}
      <div className="mt-6">
        <ForumWidget />
      </div>

      {/* Construction Section */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm font-medium text-gray-500">En construcción</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <div className="space-y-6 opacity-50">
          <EnvasesContainer data={{
            generados: 28,
            pagados: 23
          }} />
          
          <PrinterStatus printers={[
            { id: 1, name: 'Caja Principal', status: 'online' },
            { id: 2, name: 'Caja 2', status: 'offline' }
          ]} />
        </div>
      </div>
    </div>
  );
} 